import React, { useMemo, useCallback } from "react";
import WindowPattern from "./patterns/PatternSelector";

const BayDiagram = ({
  selectedValue,
  availableOptions,
  onChange,
  mode,
  disabled,
  formData,
  allAvailableOptions,
}) => {
  // Helper function to find option by keywords
  const findOption = useCallback((keywords) => {
    return availableOptions?.find((opt) => {
      const name = opt.name.toLowerCase();
      return keywords.every((keyword) => name.includes(keyword));
    });
  }, [availableOptions]);

  // Get selected end lite style
  const selectedEndLiteStyle = useMemo(() => {
    return allAvailableOptions?.end_lite_styles?.find(
      (style) => style.id === formData?.end_lite_style_id
    );
  }, [allAvailableOptions?.end_lite_styles, formData?.end_lite_style_id]);

  const isCasement = selectedEndLiteStyle?.name.toLowerCase() === "casement";

  // Get grid options
  const gridOptions = useMemo(() => ({
    topSash: findOption(["end lites", "top sash"]),
    bottomSash: findOption(["end lites", "bottom sash"]),
    bothSashes: findOption(["end lites", "both sash"]),
    full: findOption(["end lites", "full"]),
    allLitesTopSash: findOption(["all lites", "top sash"]),
    allLitesBottomSash: findOption(["all lites", "bottom sash"]),
    allLitesBothSashes: findOption(["all lites", "both sash"]),
    allLitesFull: findOption(["all lites", "full"]),
  }), [findOption]);

  // Get obscurity options (only when in obscurity mode)
  const obscurityOptions = useMemo(() => {
    if (mode !== "obscurity") return {};
    return {
      topOnly: findOption(["top only"]),
      bottomOnly: findOption(["bottom only"]),
      full: findOption(["full"]),
    };
  }, [mode, findOption]);

  // Build cycle order based on mode and style
  const cycleOrder = useMemo(() => {
    if (mode === "grid") {
      return isCasement
        ? [gridOptions.full?.id, gridOptions.allLitesFull?.id, null]
        : [
            gridOptions.topSash?.id,
            gridOptions.bottomSash?.id,
            gridOptions.bothSashes?.id,
            gridOptions.allLitesTopSash?.id,
            gridOptions.allLitesBottomSash?.id,
            gridOptions.allLitesBothSashes?.id,
            null
          ];
    }
    if (mode === "obscurity") {
      return isCasement
        ? [obscurityOptions.full?.id, null]
        : [obscurityOptions.topOnly?.id, obscurityOptions.bottomOnly?.id, obscurityOptions.full?.id, null];
    }
    return [];
  }, [mode, isCasement, gridOptions, obscurityOptions]);

  // Filter out undefined values from cycle order
  const filteredCycleOrder = useMemo(() => {
    return cycleOrder.filter((id) => id !== undefined);
  }, [cycleOrder]);

  // Helper to set pane layout (returns new layout object)
  const setPaneLayout = useCallback((layout, panes, top, bottom) => {
    const newLayout = { ...layout };
    panes.forEach((paneNum) => {
      newLayout[`pane${paneNum}`] = [top, bottom];
    });
    return newLayout;
  }, []);

  // Calculate layout based on mode and selected value
  const layout = useMemo(() => {
    const defaultLayout = {
      pane1: [false, false],
      pane2: [false, false],
      pane3: [false, false],
    };

    if (!selectedValue) return defaultLayout;

    const selectedOption = availableOptions?.find((opt) => opt.id === selectedValue);
    const optionName = selectedOption?.name.toLowerCase() || "";
    const isAllLites = optionName.includes("all lites");
    const allPanes = [1, 2, 3];
    const endPanesOnly = [1, 3]; // Panes 1 and 3 only (for "End Lites")

    if (mode === "grid") {
      // Determine which panes to apply the pattern to
      const targetPanes = isAllLites ? allPanes : endPanesOnly;

      if (optionName.includes("top sash")) {
        return setPaneLayout(defaultLayout, targetPanes, true, false);
      } else if (optionName.includes("bottom sash")) {
        return setPaneLayout(defaultLayout, targetPanes, false, true);
      } else if (optionName.includes("both sash") || optionName.includes("full")) {
        return setPaneLayout(defaultLayout, targetPanes, true, true);
      }
    } else if (mode === "obscurity") {
      if (optionName.includes("top only")) {
        return setPaneLayout(defaultLayout, allPanes, true, false);
      } else if (optionName.includes("bottom only")) {
        return setPaneLayout(defaultLayout, allPanes, false, true);
      } else if (optionName.includes("full")) {
        return setPaneLayout(defaultLayout, allPanes, true, true);
      }
    }

    return defaultLayout;
  }, [mode, selectedValue, availableOptions, setPaneLayout]);

  // Handle click to cycle through options
  const handleClick = useCallback(() => {
    if (disabled || mode === "display") return;

    const currentIndex = filteredCycleOrder.indexOf(selectedValue);
    const nextIndex = currentIndex === -1 || currentIndex === filteredCycleOrder.length - 1
      ? 0
      : currentIndex + 1;
    onChange(filteredCycleOrder[nextIndex]);
  }, [disabled, mode, filteredCycleOrder, selectedValue, onChange]);

  const selectedGridStyle = useMemo(() => {
    return allAvailableOptions?.grid_styles?.find(
      (style) => style.id === formData?.grid_style_id
    );
  }, [allAvailableOptions?.grid_styles, formData?.grid_style_id]);

  // Extract pane selection states
  const [leftTop, leftBottom] = layout.pane1;
  const [centerTop, centerBottom] = layout.pane2;
  const [rightTop, rightBottom] = layout.pane3;

  // Render a pane (single or double sash)
  const renderPane = useCallback((width, borderClass, topSelected, bottomSelected) => {
    const commonProps = {
      onClick: handleClick,
      style: { cursor: disabled || mode === "display" ? "default" : "pointer" },
    };

    if (isCasement) {
      return (
        <div
          className={`relative ${width} h-60 bg-light border-8 border-brand ${borderClass} ${disabled ? "opacity-50" : ""}`}
          {...commonProps}
        >
          <WindowPattern
            mode={mode}
            isSelected={topSelected || bottomSelected}
            gridStyleName={selectedGridStyle?.name}
          />
        </div>
      );
    }

    return (
      <>
        <div
          className={`relative ${width} h-24 bg-light border-8 border-brand ${borderClass} ${disabled ? "opacity-50" : ""}`}
          style={{ borderBottomWidth: "4px", ...commonProps.style }}
          onClick={commonProps.onClick}
        >
          <WindowPattern
            mode={mode}
            isSelected={topSelected}
            gridStyleName={selectedGridStyle?.name}
          />
        </div>
        <div
          className={`relative ${width} h-36 bg-light border-8 border-brand ${borderClass} ${disabled ? "opacity-50" : ""}`}
          style={{ borderTopWidth: "4px", ...commonProps.style }}
          onClick={commonProps.onClick}
        >
          <WindowPattern
            mode={mode}
            isSelected={bottomSelected}
            gridStyleName={selectedGridStyle?.name}
          />
        </div>
      </>
    );
  }, [isCasement, handleClick, disabled, mode, selectedGridStyle?.name]);

  return (
    <div className="flex justify-center items-center">
      <div
        className="flex relative"
        style={{
          perspective: "1000px",
          perspectiveOrigin: "center -20%",
        }}
      >
        <div>{renderPane("w-20", "border-r-0", leftTop, leftBottom)}</div>
        <div>{renderPane("w-36", "", centerTop, centerBottom)}</div>
        <div>{renderPane("w-20", "border-l-0", rightTop, rightBottom)}</div>
      </div>
    </div>
  );
};

export default BayDiagram;
